import React, { useEffect, useState } from 'react';
import { Star, ThumbsUp, CheckCircle } from 'lucide-react';
import { vendorReviewApi } from '../services/api';
import { VendorReview } from '../types';
import { extractApiError } from '../utils/errorUtils';

/**
 * VendorReviewsList Component (Issue #956)
 *
 * Display vendor reviews with pagination and filtering.
 *
 * Features:
 * - Paginated list of reviews
 * - Sort by: most helpful, most recent, highest/lowest rating
 * - Filter by rating (1-5 stars)
 * - "Helpful" voting buttons
 * - Verified purchase badge display
 * - Review date formatting
 * - Load more / pagination controls
 * - Empty state message
 */

interface VendorReviewsListProps {
  vendorId: number;
  pageSize?: number;
  ratingFilter?: number | null;
}

const VendorReviewsList: React.FC<VendorReviewsListProps> = ({
  vendorId,
  pageSize = 20,
  ratingFilter = null
}) => {
  const [reviews, setReviews] = useState<VendorReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [sortBy, setSortBy] = useState<'recent' | 'helpful' | 'rating_high' | 'rating_low'>('recent');
  const [helpfulReviews, setHelpfulReviews] = useState<Set<number>>(new Set());

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [sortBy, ratingFilter]);

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, page, sortBy, ratingFilter]);

  const fetchReviews = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await vendorReviewApi.getVendorReviews(
        vendorId,
        page,
        pageSize,
        sortBy,
        ratingFilter ?? undefined
      );
      setReviews(data.reviews);
      setTotalPages(data.total_pages);
      setTotalReviews(data.total);

      // Track which reviews user marked helpful
      const helpfulIds = new Set<number>();
      data.reviews.forEach(review => {
        if (review.user_marked_helpful) {
          helpfulIds.add(review.id);
        }
      });
      setHelpfulReviews(helpfulIds);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to fetch reviews:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkHelpful = async (reviewId: number) => {
    try {
      if (helpfulReviews.has(reviewId)) {
        // Unmark as helpful
        await vendorReviewApi.unmarkReviewHelpful(reviewId);
        setHelpfulReviews(prev => {
          const next = new Set(prev);
          next.delete(reviewId);
          return next;
        });
        // Update review count
        setReviews(prev =>
          prev.map(r =>
            r.id === reviewId ? { ...r, helpful_count: r.helpful_count - 1 } : r
          )
        );
      } else {
        // Mark as helpful
        await vendorReviewApi.markReviewHelpful(reviewId);
        setHelpfulReviews(prev => new Set(prev).add(reviewId));
        // Update review count
        setReviews(prev =>
          prev.map(r =>
            r.id === reviewId ? { ...r, helpful_count: r.helpful_count + 1 } : r
          )
        );
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to mark review helpful:', apiError);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays <= 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays <= 365) return `${Math.floor(diffDays / 30)} months ago`;

    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          className={`w-4 h-4 ${
            i <= rating ? 'text-yellow-500 fill-current' : 'text-gray-300 fill-current'
          }`}
        />
      );
    }
    return stars;
  };

  if (loading && page === 1) {
    return (
      <div className="text-center py-8 text-gray-500" data-testid="reviews-loading">
        Loading reviews...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600" data-testid="reviews-error">
        Error loading reviews: {error}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500" data-testid="reviews-empty">
        {ratingFilter
          ? `No ${ratingFilter}-star reviews yet.`
          : 'No reviews yet. Be the first to leave a review!'}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="vendor-reviews-list">
      {/* Sort controls */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {totalReviews} {totalReviews === 1 ? 'Review' : 'Reviews'}
        </h3>
        <div className="flex items-center gap-2">
          <label htmlFor="sort-select" className="text-sm text-gray-600">
            Sort by:
          </label>
          <select
            id="sort-select"
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as typeof sortBy);
              setPage(1);
            }}
            className="border border-gray-300 rounded px-3 py-1 text-sm"
            data-testid="sort-select"
          >
            <option value="recent">Most Recent</option>
            <option value="helpful">Most Helpful</option>
            <option value="rating_high">Highest Rating</option>
            <option value="rating_low">Lowest Rating</option>
          </select>
        </div>
      </div>

      {/* Reviews list */}
      <div className="space-y-4">
        {reviews.map((review) => (
          <div
            key={review.id}
            className="border border-gray-200 rounded-lg p-4 bg-white"
            data-testid={`review-${review.id}`}
          >
            {/* Review header */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-gray-900" data-testid="customer-name">
                    {review.customer_display_name || 'Anonymous'}
                  </span>
                  {review.verified_purchase && (
                    <div
                      className="flex items-center gap-1 bg-teal-100 text-primary-dark px-2 py-0.5 rounded text-xs font-medium"
                      data-testid="verified-badge"
                    >
                      <CheckCircle className="w-3 h-3" />
                      <span>Verified Purchase</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-0.5" data-testid="review-rating">
                    {renderStars(review.rating)}
                  </div>
                  <span className="text-sm text-gray-500" data-testid="review-date">
                    {formatDate(review.created_at)}
                  </span>
                  {review.fair_name && (
                    <span className="text-sm text-gray-400">• {review.fair_name}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Review text */}
            {review.review_text && (
              <p className="text-gray-700 mb-3" data-testid="review-text">
                {review.review_text}
              </p>
            )}

            {/* Helpful button */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleMarkHelpful(review.id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded border text-sm transition-colors ${
                  helpfulReviews.has(review.id)
                    ? 'bg-moonbone border-teal-300 text-primary-dark'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`helpful-button-${review.id}`}
              >
                <ThumbsUp className="w-4 h-4" />
                <span>Helpful</span>
                {review.helpful_count > 0 && (
                  <span className="font-semibold">({review.helpful_count})</span>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            data-testid="prev-page-button"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600" data-testid="page-info">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            data-testid="next-page-button"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default VendorReviewsList;
